import React, { createContext, useContext, useState, useCallback } from 'react';

const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      platform: 'Platform',
      positiveReviews: 'Positive Reviews',
      reviewShield: 'B-Heard',
      globalReview: 'Global Review',
      rrr360: 'R&R 360 — Reward & Resolve',
      guestCommunication: 'Guest Communication',
      insights: 'Insights',
      hardware: 'Hardware',
      implementation: 'Implementation',
      portal: 'Portal',
      marco: 'Marcom',
      contact: 'Contact',
      supportingData: 'Supporting Data',
      getStarted: 'Get Started',
      requestDemo: 'Request a Demo',
      login: 'Login',
      menu: 'Menu',
    },
    // Home page
    home: {
      heroTitle: "Know What's Happening With Your Clients, Guests & Customers —",
      heroAccent: "In Real Time.",
      heroSubtitle: 'YUL On Tap gives businesses a direct communication line with their clients while providing real-time feedback, reputation management, and operational insights.',
      cta: 'Get Started',
      ctaSecondary: 'Request a Demo',
      industryHeading: 'CREATED FOR BUSINESSES THAT CARE ABOUT THEIR CLIENTS',
      capabilitiesTitle: 'Platform Capabilities',
      capabilitiesSubtitle: 'Everything you need to deliver exceptional client experiences',
      platformTitle: 'The Platform',
      platformDesc: 'Real-time communication between clients, staff, and management.',
      reviewsTitle: 'Positive Reviews',
      reviewsDesc: 'Capture positive experiences and strengthen your reputation.',
      shieldTitle: 'B-Heard',
      shieldDesc: 'Give guests a private voice and the chance to make it right before it goes public.',
      rr360Title: 'R&R 360 — Reward & Resolve',
      rr360Desc: 'Empower staff to recognize clients and resolve service moments.',
      communicationTitle: 'Real-Time Communication',
      communicationDesc: 'Direct line between clients and your team.',
      insightsTitle: 'Insights & Reporting',
      insightsDesc: 'Operational intelligence with trends and performance signals.',
      hardwareTitle: 'Hardware',
      hardwareDesc: 'Tap Engine Cards with custom branding.',
      implementationTitle: 'Implementation',
      implementationDesc: 'Simple setup, minimal training, fast rollout.',
      ctaTitle: 'Ready to Transform Your Client Experience?',
      ctaSubtitle: 'Join businesses that are already using YUL On Tap to connect with their clients.',
      supportingDataHeading: 'Research-Backed Platform',
      supportingDataText: 'Every capability in our platform is informed by academic research, industry data, and real-world evidence across multiple verticals.',
      supportingDataCta: 'View Supporting Data',
    },
    // Platform page
    platform: {
      title: 'The Platform',
      subtitle: 'A complete operational system for modern restaurants',
      heroTitle: 'Real-Time Guest Intelligence',
      heroDesc: 'YUL On Tap connects every table to your management team, enabling instant feedback, service recovery, and operational visibility.',
      feature1Title: 'Guest-to-Staff Communication',
      feature1Desc: 'Every interaction captured and routed to the right person.',
      feature2Title: 'Management Visibility',
      feature2Desc: 'Real-time dashboards show what\'s happening across your operation.',
      feature3Title: 'Operational Awareness',
      feature3Desc: 'Stay ahead of issues before they become problems.',
    },
    // Positive Reviews page
    positiveReviews: {
      title: 'Positive Reviews',
      subtitle: 'Turn great experiences into public recognition',
      heroDesc: 'Capture positive guest experiences at the moment they happen and encourage satisfied customers to share their stories.',
      howItWorks: 'How It Works',
      step1Title: 'Capture Feedback',
      step1Desc: 'Guests share their positive experiences directly through your table tap system.',
      step2Title: 'Encourage Sharing',
      step2Desc: 'Happy guests are prompted to share on Google, Yelp, and other platforms.',
      step3Title: 'Build Reputation',
      step3Desc: 'Watch your online presence grow with authentic, positive reviews.',
      benefitsTitle: 'Benefits',
      benefit1: 'Increase positive review volume',
      benefit2: 'Improve search rankings',
      benefit3: 'Build customer trust',
      benefit4: 'Stand out from competitors',
    },
    // B-Heard page (formerly Review Shield)
    reviewShield: {
      title: 'B-Heard',
      subtitle: 'Hear them first. Make it right before it goes public.',
      heroDesc: 'Give guests a private way to tell you what went wrong, so your team can make it right while they\'re still with you — before it ever needs to go public.',
      feature1Title: 'Private Feedback Capture',
      feature1Desc: 'Guests can tell you privately or post a public review. It\'s always their choice.',
      feature2Title: 'Immediate Notification',
      feature2Desc: 'Staff receive instant alerts to address issues in real-time.',
      feature3Title: 'Service Recovery',
      feature3Desc: 'Resolve concerns while the guest is still with you, so they never feel they have to take it online.',
      positiveTitle: 'Create public brand ambassadors',
      positiveDesc: 'Turn satisfied guests into vocal advocates who share their experience on Google and beyond.',
      negativeTitle: 'A private channel for instant guest recovery',
      negativeDesc: 'Hear concerns privately, alert staff in real time, and make it right before it goes public.',
    },
    // Global Review page
    globalReview: {
      title: 'Global Review',
      subtitle: 'Visibility across all your locations',
      heroDesc: 'Monitor performance, track trends, and understand guest sentiment across every restaurant in your organization.',
      feature1Title: 'Performance Comparison',
      feature1Desc: 'Compare metrics across locations to identify best practices.',
      feature2Title: 'Trend Tracking',
      feature2Desc: 'Spot patterns and address issues before they spread.',
      feature3Title: 'Sentiment Monitoring',
      feature3Desc: 'Understand how guests feel about each location.',
      dashboardTitle: 'All feedback. Every location. One view.',
      dashboardDesc: 'Track ratings, messages, and service categories across all your restaurants in real-time.',
    },
    // R&R 360 page
    rr360: {
      title: 'R&R 360 — Reward & Resolve',
      subtitle: 'Empower your staff to create memorable moments',
      heroDesc: 'Give your team the tools to recognize exceptional guests and resolve service challenges on the spot.',
      feature1Title: 'Recognize Guests',
      feature1Desc: 'Celebrate loyal customers and special occasions.',
      feature2Title: 'Resolve Moments',
      feature2Desc: 'Turn potential negatives into positive experiences.',
      feature3Title: 'Strengthen Relationships',
      feature3Desc: 'Build lasting connections with your guests.',
    },
    // Guest Communication page
    guestCommunication: {
      title: 'Guest Communication',
      subtitle: 'The communication engine for modern hospitality',
      heroDesc: 'Enable feedback, complaints, suggestions, menu input, and service requests—all through a single, elegant system.',
      feature1Title: 'Feedback',
      feature1Desc: 'Collect guest opinions in real-time.',
      feature2Title: 'Complaints',
      feature2Desc: 'Address issues immediately before they escalate.',
      feature3Title: 'Suggestions',
      feature3Desc: 'Gather ideas directly from your customers.',
      feature4Title: 'Menu Input',
      feature4Desc: 'Let guests share their menu preferences.',
      feature5Title: 'Service Requests',
      feature5Desc: 'Handle special requests efficiently.',
      feature6Title: 'Events & Bookings',
      feature6Desc: 'Manage reservations and event coordination seamlessly.',
    },
    // Insights page
    insights: {
      title: 'Insights & Reporting',
      subtitle: 'Operational intelligence for better decisions',
      heroDesc: 'Transform guest feedback into actionable insights with trends, performance signals, and operational visibility.',
      feature1Title: 'Trends',
      feature1Desc: 'Identify patterns in guest behavior and feedback.',
      feature2Title: 'Performance Signals',
      feature2Desc: 'Spot opportunities and issues early.',
      feature3Title: 'Operational Visibility',
      feature3Desc: 'See the complete picture of your restaurant operations.',
    },
    // Hardware page
    hardware: {
      title: 'Hardware',
      subtitle: 'The physical Tap engine',
      heroDesc: 'Premium Tap Engine Cards designed to match your brand, deployed at every table.',
      feature1Title: 'Tap Engine Cards',
      feature1Desc: 'Instant tap access for guests with any compatible device.',
      feature2Title: 'Universal Access',
      feature2Desc: 'Works with any smartphone — no app download required.',
      feature3Title: 'Custom Branding',
      feature3Desc: 'Cards designed to match your restaurant\'s identity.',
    },
    // Implementation page
    implementation: {
      title: 'Implementation',
      subtitle: 'Simple deployment, immediate results',
      heroDesc: 'Get up and running quickly with minimal disruption to your operations.',
      feature1Title: 'Simple Setup',
      feature1Desc: 'No complex installations or technical requirements.',
      feature2Title: 'Minimal Training',
      feature2Desc: 'Staff can start using the system immediately.',
      feature3Title: 'Fast Rollout',
      feature3Desc: 'Go live across all locations in days, not months.',
      feature4Title: 'No App Required',
      feature4Desc: 'Guests use their existing smartphones—no downloads needed.',
    },
    // Portal page
    portal: {
      title: 'Portal Login',
      subtitle: 'Access your dashboard',
      emailLabel: 'Email',
      emailPlaceholder: 'Enter your email',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter your password',
      loginButton: 'Login',
      forgotPassword: 'Forgot password?',
      noAccount: 'Don\'t have an account?',
      contactUs: 'Contact us',
    },
    // Contact page
    contact: {
      title: 'Request a Demo',
      subtitle: 'See how YUL On Tap can transform your restaurant',
      nameLabel: 'Name',
      namePlaceholder: 'Your name',
      companyLabel: 'Restaurant / Company',
      companyPlaceholder: 'Restaurant or company name',
      emailLabel: 'Email',
      emailPlaceholder: 'your@email.com',
      phoneLabel: 'Phone',
      phonePlaceholder: 'Your phone number',
      messageLabel: 'Message',
      messagePlaceholder: 'Tell us about your restaurant and what you\'re looking for...',
      submitButton: 'Send Request',
      successMessage: 'Thank you! We\'ll be in touch soon.',
      errorMessage: 'Something went wrong. Please try again.',
    },
    // Supporting Data page
    supportingData: {
      title: 'Supporting Data',
      subtitle: 'The research and evidence behind our platform',
      intro: 'We believe transparency builds trust. Below is a curated selection of academic research, industry findings, consumer data, and our own models that inform the YUL On Tap platform.',
      tabHotels: 'Hotels',
      tabRestaurants: 'Restaurants',
      tabMedical: 'Medical Aesthetics',
      classAcademic: 'Academic Research',
      classIndustry: 'Industry Research',
      classConsumer: 'Consumer Data',
      classModel: 'YUL ON TAP Model',
      sourceLabel: 'Source',
      sourcesLabel: 'Sources',
      methodologyTitle: 'Methodology Note',
      methodologyText: 'Where YUL ON TAP models are presented, the underlying assumptions and inputs are clearly stated. Published research findings are attributed to their original authors and publications. All models use stated assumptions and should be evaluated accordingly.',
    },
    // Footer
    footer: {
      tagline: 'Turning every table into a direct communication line.',
      copyright: '© 2024 YUL On Tap. All rights reserved.',
      capabilities: 'Capabilities',
      company: 'Company',
      connect: 'Connect',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
    },
  },
  fr: {
    // Navigation
    nav: {
      home: 'Accueil',
      platform: 'Plateforme',
      positiveReviews: 'Avis Positifs',
      reviewShield: 'B-Heard',
      globalReview: 'Avis Global',
      rrr360: 'R&R 360 — Récompense & Résolution',
      guestCommunication: 'Communication Invités',
      insights: 'Aperçus',
      hardware: 'Matériel',
      implementation: 'Implémentation',
      portal: 'Portail',
      contact: 'Contact',
      supportingData: 'Données Probantes',
      getStarted: 'Commencer',
      requestDemo: 'Demander une Démo',
      login: 'Connexion',
      menu: 'Menu',
    },
    // Home page
    home: {
      heroTitle: 'Sachez Ce Qui Se Passe Avec Vos Clients, Invités et Consommateurs —',
      heroAccent: 'En Temps Réel.',
      heroSubtitle: 'YUL On Tap offre aux entreprises une ligne de communication directe avec leurs clients, fournissant des commentaires en temps réel, la gestion de la réputation et des informations opérationnelles.',
      cta: 'Commencer',
      ctaSecondary: 'Demander une Démo',
      industryHeading: 'CRÉÉ POUR LES ENTREPRISES QUI SE SOUCIENT DE LEURS CLIENTS',
      capabilitiesTitle: 'Capacités de la Plateforme',
      capabilitiesSubtitle: 'Tout ce dont vous avez besoin pour offrir des expériences client exceptionnelles',
      platformTitle: 'La Plateforme',
      platformDesc: 'Communication en temps réel entre clients, personnel et direction.',
      reviewsTitle: 'Avis Positifs',
      reviewsDesc: 'Capturez les expériences positives et renforcez votre réputation.',
      shieldTitle: 'B-Heard',
      shieldDesc: 'Offrez aux clients une voix privée et la chance de régler la situation avant qu\'elle ne devienne publique.',
      rr360Title: 'R&R 360 — Récompense & Résolution',
      rr360Desc: 'Permettez au personnel de reconnaître les clients et résoudre les situations.',
      communicationTitle: 'Communication en Temps Réel',
      communicationDesc: 'Ligne directe entre les clients et votre équipe.',
      insightsTitle: 'Aperçus & Rapports',
      insightsDesc: 'Intelligence opérationnelle avec tendances et signaux de performance.',
      hardwareTitle: 'Matériel',
      hardwareDesc: 'Cartes Tap Engine avec image de marque personnalisée.',
      implementationTitle: 'Implémentation',
      implementationDesc: 'Configuration simple, formation minimale, déploiement rapide.',
      ctaTitle: 'Prêt à Transformer l\'Expérience de Vos Clients?',
      ctaSubtitle: 'Rejoignez les entreprises qui utilisent déjà YUL On Tap pour connecter avec leurs clients.',
      supportingDataHeading: 'Plateforme Appuyée par la Recherche',
      supportingDataText: 'Chaque capacité de notre plateforme est éclairée par la recherche académique, les données industrielles et les preuves concrètes dans de multiples secteurs.',
      supportingDataCta: 'Voir les Données Probantes',
    },
    // Platform page
    platform: {
      title: 'La Plateforme',
      subtitle: 'Un système opérationnel complet pour les restaurants modernes',
      heroTitle: 'Intelligence Invités en Temps Réel',
      heroDesc: 'YUL On Tap connecte chaque table à votre équipe de gestion, permettant des retours instantanés, la récupération de service et la visibilité opérationnelle.',
      feature1Title: 'Communication Invité-Personnel',
      feature1Desc: 'Chaque interaction capturée et acheminée à la bonne personne.',
      feature2Title: 'Visibilité de la Direction',
      feature2Desc: 'Des tableaux de bord en temps réel montrent ce qui se passe dans votre opération.',
      feature3Title: 'Conscience Opérationnelle',
      feature3Desc: 'Anticipez les problèmes avant qu\'ils ne surviennent.',
    },
    // Positive Reviews page
    positiveReviews: {
      title: 'Avis Positifs',
      subtitle: 'Transformez les grandes expériences en reconnaissance publique',
      heroDesc: 'Capturez les expériences positives des invités au moment où elles se produisent et encouragez les clients satisfaits à partager leurs histoires.',
      howItWorks: 'Comment Ça Fonctionne',
      step1Title: 'Capturer les Commentaires',
      step1Desc: 'Les invités partagent leurs expériences positives directement via votre système de tap de table.',
      step2Title: 'Encourager le Partage',
      step2Desc: 'Les invités satisfaits sont invités à partager sur Google, Yelp et d\'autres plateformes.',
      step3Title: 'Construire la Réputation',
      step3Desc: 'Regardez votre présence en ligne grandir avec des avis positifs authentiques.',
      benefitsTitle: 'Avantages',
      benefit1: 'Augmenter le volume d\'avis positifs',
      benefit2: 'Améliorer les classements de recherche',
      benefit3: 'Construire la confiance des clients',
      benefit4: 'Se démarquer des concurrents',
    },
    // B-Heard page (formerly Review Shield)
    reviewShield: {
      title: 'B-Heard',
      subtitle: 'Écoutez-les d\'abord. Réglez la situation avant qu\'elle ne devienne publique.',
      heroDesc: 'Offrez à vos clients un moyen privé de vous dire ce qui n\'a pas fonctionné, pour que votre équipe puisse arranger les choses pendant qu\'ils sont encore là — avant que ça ne se retrouve en ligne.',
      feature1Title: 'Capture Privée des Commentaires',
      feature1Desc: 'Les clients peuvent vous écrire en privé ou publier un avis public. C\'est toujours leur choix.',
      feature2Title: 'Notification Immédiate',
      feature2Desc: 'Le personnel reçoit des alertes instantanées pour résoudre les problèmes en temps réel.',
      feature3Title: 'Récupération de Service',
      feature3Desc: 'Réglez les préoccupations pendant que le client est encore avec vous, pour qu\'il ne sente jamais le besoin de passer en ligne.',
      positiveTitle: 'Créez des ambassadeurs publics de votre marque',
      positiveDesc: 'Transformez les invités satisfaits en défenseurs vocaux qui partagent leur expérience sur Google et au-delà.',
      negativeTitle: 'Un canal privé pour une récupération instantanée',
      negativeDesc: 'Entendez les préoccupations en privé, alertez le personnel en temps réel et réglez la situation avant qu\'elle ne devienne publique.',
    },
    // Global Review page
    globalReview: {
      title: 'Avis Global',
      subtitle: 'Visibilité sur tous vos emplacements',
      heroDesc: 'Surveillez la performance, suivez les tendances et comprenez le sentiment des invités dans chaque restaurant de votre organisation.',
      feature1Title: 'Comparaison de Performance',
      feature1Desc: 'Comparez les métriques entre les emplacements pour identifier les meilleures pratiques.',
      feature2Title: 'Suivi des Tendances',
      feature2Desc: 'Repérez les patterns et adressez les problèmes avant qu\'ils ne se propagent.',
      feature3Title: 'Surveillance du Sentiment',
      feature3Desc: 'Comprenez comment les invités perçoivent chaque emplacement.',
      dashboardTitle: 'Tous les commentaires. Chaque emplacement. Une seule vue.',
      dashboardDesc: 'Suivez les évaluations, les messages et les catégories de service dans tous vos restaurants en temps réel.',
    },
    // R&R 360 page
    rr360: {
      title: 'R&R 360 — Récompense & Résolution',
      subtitle: 'Donnez à votre personnel les moyens de créer des moments mémorables',
      heroDesc: 'Donnez à votre équipe les outils pour reconnaître les invités exceptionnels et résoudre les défis de service sur place.',
      feature1Title: 'Reconnaître les Invités',
      feature1Desc: 'Célébrez les clients fidèles et les occasions spéciales.',
      feature2Title: 'Résoudre les Moments',
      feature2Desc: 'Transformez les potentiels négatifs en expériences positives.',
      feature3Title: 'Renforcer les Relations',
      feature3Desc: 'Construisez des connexions durables avec vos invités.',
    },
    // Guest Communication page
    guestCommunication: {
      title: 'Communication Invités',
      subtitle: 'Le moteur de communication pour l\'hospitalité moderne',
      heroDesc: 'Permettez les commentaires, plaintes, suggestions, avis sur le menu et demandes de service—le tout via un système unique et élégant.',
      feature1Title: 'Commentaires',
      feature1Desc: 'Collectez les opinions des invités en temps réel.',
      feature2Title: 'Plaintes',
      feature2Desc: 'Adressez les problèmes immédiatement avant qu\'ils n\'escaladent.',
      feature3Title: 'Suggestions',
      feature3Desc: 'Recueillez des idées directement de vos clients.',
      feature4Title: 'Avis sur le Menu',
      feature4Desc: 'Laissez les invités partager leurs préférences de menu.',
      feature5Title: 'Demandes de Service',
      feature5Desc: 'Gérez les demandes spéciales efficacement.',
      feature6Title: 'Événements & Réservations',
      feature6Desc: 'Gérez les réservations et la coordination d\'événements en toute simplicité.',
    },
    // Insights page
    insights: {
      title: 'Aperçus & Rapports',
      subtitle: 'Intelligence opérationnelle pour de meilleures décisions',
      heroDesc: 'Transformez les commentaires des invités en aperçus actionnables avec des tendances, des signaux de performance et une visibilité opérationnelle.',
      feature1Title: 'Tendances',
      feature1Desc: 'Identifiez les patterns dans le comportement et les commentaires des invités.',
      feature2Title: 'Signaux de Performance',
      feature2Desc: 'Repérez les opportunités et les problèmes tôt.',
      feature3Title: 'Visibilité Opérationnelle',
      feature3Desc: 'Voyez l\'image complète de vos opérations de restaurant.',
    },
    // Hardware page
    hardware: {
      title: 'Matériel',
      subtitle: 'Le moteur Tap physique',
      heroDesc: 'Cartes Tap Engine premium conçues pour correspondre à votre marque, déployées à chaque table.',
      feature1Title: 'Cartes Tap Engine',
      feature1Desc: 'Accès tap instantané pour les invités avec tout appareil compatible.',
      feature2Title: 'Accès Universel',
      feature2Desc: 'Fonctionne avec tout smartphone — aucun téléchargement d\'application requis.',
      feature3Title: 'Image de Marque Personnalisée',
      feature3Desc: 'Cartes conçues pour correspondre à l\'identité de votre restaurant.',
    },
    // Implementation page
    implementation: {
      title: 'Implémentation',
      subtitle: 'Déploiement simple, résultats immédiats',
      heroDesc: 'Démarrez rapidement avec une perturbation minimale de vos opérations.',
      feature1Title: 'Configuration Simple',
      feature1Desc: 'Pas d\'installations complexes ni d\'exigences techniques.',
      feature2Title: 'Formation Minimale',
      feature2Desc: 'Le personnel peut commencer à utiliser le système immédiatement.',
      feature3Title: 'Déploiement Rapide',
      feature3Desc: 'Passez en direct sur tous les emplacements en jours, pas en mois.',
      feature4Title: 'Pas d\'Application Requise',
      feature4Desc: 'Les invités utilisent leurs smartphones existants—pas de téléchargements nécessaires.',
    },
    // Portal page
    portal: {
      title: 'Connexion au Portail',
      subtitle: 'Accédez à votre tableau de bord',
      emailLabel: 'Courriel',
      emailPlaceholder: 'Entrez votre courriel',
      passwordLabel: 'Mot de passe',
      passwordPlaceholder: 'Entrez votre mot de passe',
      loginButton: 'Connexion',
      forgotPassword: 'Mot de passe oublié?',
      noAccount: 'Vous n\'avez pas de compte?',
      contactUs: 'Contactez-nous',
    },
    // Contact page
    contact: {
      title: 'Demander une Démo',
      subtitle: 'Découvrez comment YUL On Tap peut transformer votre restaurant',
      nameLabel: 'Nom',
      namePlaceholder: 'Votre nom',
      companyLabel: 'Restaurant / Entreprise',
      companyPlaceholder: 'Nom du restaurant ou de l\'entreprise',
      emailLabel: 'Courriel',
      emailPlaceholder: 'votre@courriel.com',
      phoneLabel: 'Téléphone',
      phonePlaceholder: 'Votre numéro de téléphone',
      messageLabel: 'Message',
      messagePlaceholder: 'Parlez-nous de votre restaurant et de ce que vous recherchez...',
      submitButton: 'Envoyer la Demande',
      successMessage: 'Merci! Nous vous contacterons bientôt.',
      errorMessage: 'Une erreur s\'est produite. Veuillez réessayer.',
    },
    // Supporting Data page
    supportingData: {
      title: 'Données Probantes',
      subtitle: 'La recherche et les preuves derrière notre plateforme',
      intro: 'Nous croyons que la transparence renforce la confiance. Voici une sélection de recherches académiques, de résultats industriels, de données consommateurs et de nos propres modèles qui éclairent la plateforme YUL On Tap.',
      tabHotels: 'Hôtels',
      tabRestaurants: 'Restaurants',
      tabMedical: 'Esthétique Médicale',
      classAcademic: 'Recherche Académique',
      classIndustry: 'Recherche Industrielle',
      classConsumer: 'Données Consommateurs',
      classModel: 'Modèle YUL ON TAP',
      sourceLabel: 'Source',
      sourcesLabel: 'Sources',
      methodologyTitle: 'Note Méthodologique',
      methodologyText: 'Lorsque des modèles YUL ON TAP sont présentés, les hypothèses et paramètres sous-jacents sont clairement énoncés. Les résultats de recherche publiés sont attribués à leurs auteurs et publications d\'origine. Tous les modèles utilisent des hypothèses énoncées et doivent être évalués en conséquence.',
    },
    // Footer
    footer: {
      tagline: 'Transformant chaque table en ligne de communication directe.',
      copyright: '© 2024 YUL On Tap. Tous droits réservés.',
      capabilities: 'Capacités',
      company: 'Entreprise',
      connect: 'Connecter',
      privacy: 'Politique de Confidentialité',
      terms: 'Conditions d\'Utilisation',
    },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  }, [language]);

  const toggleLanguage = useCallback(() => {
    setLanguage(prev => prev === 'en' ? 'fr' : 'en');
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
